import React, { useRef, useState } from 'react';
import ContactsSelectionList, {
  ContactSelectionListRef,
} from './ContactSelectionList';
import Flex from '../../../components/Flex';
import {
  Routes as ReactRouterDomRoutes,
  Route,
  useParams,
} from 'react-router-dom';
import ContactDetailsPage from './ContactDetailsPage';
import CompanyDetailsPage from '../../companies/components/CompanyDetailsPage';
import { Typography } from 'antd';
import { Button } from '@prio365/prio365-react-library';
import { Trans, useTranslation } from 'react-i18next';
import { ContactSearchType } from '../../../models/Types';
import NavigationBar from '../../../components/NavigationBar';
import { useDispatch } from 'react-redux';
import { openContactsDrawer } from '../actions/drawer';
import {
  useExportContactsToExcelFile,
  useExportCompaniesToExcelFile,
} from '../export';
import useAccessRights from '../../users/hooks/useAccessRights';
import * as Sentry from '@sentry/react';

import { makePrioStyles } from '../../../theme/utils';
import { Contact } from '../../../models/Contact';
import { Company } from '../../../models/Company';
import ContactTagsProvider from './ContactTagsProvider';
const Routes = Sentry.withSentryReactRouterV6Routing(ReactRouterDomRoutes);

const useStyles = makePrioStyles((theme) => ({
  root: {
    position: 'relative',
    height: '100%',
  },
  moduleRoot: {
    height: '100%',
    overflow: 'hidden',
  },
  list: {
    borderRight: theme.old.borders.sub,
  },
  content: {
    overflow: 'hidden',
  },
}));

interface ContactsModuleProps {}

export const ContactsModule: React.FC<ContactsModuleProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { selectedList } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { exportContact: canExportContact, exportCompany: canExportCompany } =
    useAccessRights(['exportContact', 'exportCompany']);

  const [hasItemSelected, setHasItemSelected] = useState<boolean>(false);

  const contactSelectionListRef = useRef<ContactSelectionListRef>(null);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const showNewContactDrawer = () =>
    dispatch(openContactsDrawer({ view: 'contactNew' }));

  const exportContactsToExcelFile = useExportContactsToExcelFile();
  const exportCompaniesToExcelFile = useExportCompaniesToExcelFile();

  const exportToExcelFile = () => {
    const selectedContacts =
      contactSelectionListRef.current
        ?.getSelectedItems()
        ?.filter((item) => item.type === 'contact')
        ?.map((item) => item.value as Contact) ?? [];
    const selectedCompanies =
      contactSelectionListRef.current
        ?.getSelectedItems()
        ?.filter((item) => item.type === 'company')
        ?.map((item) => item.value as Company) ?? [];
    switch (selectedList) {
      case 'all': {
        if (selectedContacts?.length > 0) {
          exportContactsToExcelFile(selectedContacts);
        }
        if (selectedCompanies?.length > 0) {
          exportCompaniesToExcelFile(selectedCompanies);
        }
        break;
      }
      case 'allContacts': {
        if (selectedContacts?.length > 0) {
          exportContactsToExcelFile(selectedContacts);
        }
        break;
      }
      case 'allCompanies': {
        if (selectedCompanies?.length > 0) {
          exportCompaniesToExcelFile(selectedCompanies);
        }
        break;
      }
    }
  };

  const handleOnExportClick = () => {
    exportToExcelFile();
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <Flex.Column className={classes.root} id="prio-contacts-module">
      <NavigationBar>
        <Button onClick={showNewContactDrawer} iconProp={['fal', 'user-plus']}>
          <span>{t('contacts:navigationBar.createContact')}</span>
        </Button>
        <Button
          type="default"
          disabled={!(canExportContact && canExportCompany) || !hasItemSelected}
          onClick={handleOnExportClick}
          iconProp={['fal', 'file-excel']}
        >
          <span>{t('contacts:navigationBar.exportToExcelFile')}</span>
        </Button>
      </NavigationBar>
      <Flex.Row flex={1} className={classes.moduleRoot}>
        <ContactsSelectionList
          type={selectedList as ContactSearchType}
          onHasItemSelected={setHasItemSelected}
          className={classes.list}
          ref={contactSelectionListRef}
        />
        <Flex.Item flex={1} className={classes.content}>
          <ContactTagsProvider>
            <Routes>
              <Route
                path="contact/:contactId/details"
                element={<ContactDetailsPage />}
              />
              <Route
                path="company/:companyId/details"
                element={<CompanyDetailsPage />}
              />
              <Route
                path="/"
                element={
                  <div className="prio-flex-center-center prio-flex-column">
                    <Typography.Text>
                      <Trans
                        i18nKey={'contacts:contactDetails.noContactSelected'}
                      />
                    </Typography.Text>
                  </div>
                }
              />
            </Routes>
          </ContactTagsProvider>
        </Flex.Item>
      </Flex.Row>
    </Flex.Column>
  );
};

export default ContactsModule;
